/**
 * Service worker registration and push subscription management.
 */

/** Register the service worker. Call once on app mount. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return registration;
  } catch {
    return null;
  }
}

/** Check if push is supported on this browser/device. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.serviceWorker &&
    typeof window !== 'undefined' &&
    !!window.PushManager &&
    !!window.Notification
  );
}

/** Check current notification permission state. */
export function getPermissionState(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !window.Notification) return 'unsupported';
  return Notification.permission;
}

/** Request notification permission from the browser. */
export async function requestPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission();
}

/**
 * Subscribe to push notifications.
 * Returns the PushSubscription JSON to send to the backend, or null on failure.
 */
export async function subscribeToPush(
  vapidPublicKey: string
): Promise<PushSubscriptionJSON | null> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
    });
    return subscription.toJSON();
  } catch {
    return null;
  }
}

/** Unsubscribe from push notifications. */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      return subscription.unsubscribe();
    }
    return true;
  } catch {
    return false;
  }
}

/** Get current push subscription if any. */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Convert VAPID public key from base64 URL to Uint8Array. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}
